import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/types';
import { apiFetch } from './api';

/**
 * Passkey browser flows. Two paths:
 *
 *   - registerPasskey(deviceName): logged-in user enrolls a new device.
 *     Calls /register/options → browser create() → /register/verify.
 *
 *   - loginWithPasskey(): unauthenticated. Calls /login/options →
 *     browser get() → /login/verify, returns a Firebase custom token
 *     to feed into signInWithCustomToken.
 */

export function isPasskeySupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function registerPasskey(deviceName?: string): Promise<void> {
  const options = await apiFetch<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/passkey/register/options',
    { method: 'POST' },
  );
  const cred: RegistrationResponseJSON = await startRegistration({ optionsJSON: options });
  await apiFetch('/api/auth/passkey/register/verify', {
    method: 'POST',
    body: JSON.stringify({ response: cred, deviceName }),
  });
}

export async function loginWithPasskey(): Promise<{ customToken: string }> {
  const options = await apiFetch<
    PublicKeyCredentialRequestOptionsJSON & { anonId: string }
  >('/api/auth/passkey/login/options', { method: 'POST' });
  const { anonId, ...rest } = options;
  const cred: AuthenticationResponseJSON = await startAuthentication({ optionsJSON: rest });
  return apiFetch<{ customToken: string }>('/api/auth/passkey/login/verify', {
    method: 'POST',
    body: JSON.stringify({ response: cred, anonId }),
  });
}

export type PasskeyRow = {
  id: string;
  credentialId: string;
  deviceName: string | null;
  transports: string[];
  lastUsedAt: string | null;
  createdAt: string;
};

export async function listPasskeys(): Promise<PasskeyRow[]> {
  const res = await apiFetch<{ items: PasskeyRow[] }>('/api/auth/passkey/list');
  return res.items;
}

export async function deletePasskey(id: string): Promise<void> {
  await apiFetch(`/api/auth/passkey/${id}`, { method: 'DELETE' });
}
